import { ConfigProvider, Select, Tabs, TabsProps } from 'antd';
import React, { useState, useMemo, useEffect } from 'react';
import { Chart } from '@mm/components';
import styles from './SignalGraph.module.css';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export const signalRangeSelectOptions = [
  {
    value: 'week' as const,
    label: 'Last Week',
  },
  {
    value: 'month' as const,
    label: 'Last Month',
  },
  {
    value: '3 months' as const,
    label: 'Last 3 Months',
  },
];

export const SignalGraph = ({
  selectedRangeOption,
  setSelectedRangeOption,
  data,
  activeTab,
  configuration,
}: {
  selectedRangeOption: string;
  setSelectedRangeOption: (
    value: typeof signalRangeSelectOptions[0]['value'],
  ) => void;
  data: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      borderColor: string;
      fill: boolean;
    }[];
  } | null;
  activeTab: 'all' | 'new' | 'existing';
  configuration?: {
    integrations?: string[];
    mode?: string;
  };
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState('1');

  const hasKlaviyo = configuration?.integrations?.includes('klaviyo');
  const isManual = configuration?.mode === 'manual';

  const items: TabsProps['items'] = useMemo(
    () => [
      {
        key: '1',
        label: 'All Events',
      },
      // Show "Website visit" as the first option for manual mode
      ...(isManual
        ? [
            {
              key: '2',
              label: 'Website visit',
            },
          ]
        : []),
      // Only show "Active On Site" if Klaviyo is enabled (adjust key based on whether website visit is shown)
      ...(hasKlaviyo
        ? [
            {
              key: isManual ? '3' : '2',
              label: 'Active On Site',
            },
          ]
        : []),
      {
        key: isManual ? (hasKlaviyo ? '4' : '3') : hasKlaviyo ? '3' : '2',
        label: 'Product View',
      },
      {
        key: isManual ? (hasKlaviyo ? '5' : '4') : hasKlaviyo ? '4' : '3',
        label: 'Collection View',
      },
      {
        key: isManual ? (hasKlaviyo ? '6' : '5') : hasKlaviyo ? '5' : '4',
        label: 'Add to Cart',
      },
      {
        key: isManual ? (hasKlaviyo ? '7' : '6') : hasKlaviyo ? '6' : '5',
        label: 'Checkout Started',
      },
      // Show "Return Visit" for both manual and klaviyoShopify modes (not conditional)
      {
        key: isManual ? (hasKlaviyo ? '8' : '7') : hasKlaviyo ? '7' : '6',
        label: 'Return Visit',
      },
      // Show "Tracking Gap Klaviyo" as the last tab if Klaviyo is enabled
      ...(hasKlaviyo
        ? [
            {
              key: isManual ? '9' : '8',
              label: 'Tracking Gap Klaviyo',
            },
          ]
        : []),
    ],
    [hasKlaviyo, isManual],
  );

  // Reset internal active tab if current tab is no longer available
  useEffect(() => {
    const availableKeys = items.map(item => item?.key).filter(Boolean);
    if (!availableKeys.includes(internalActiveTab)) {
      setInternalActiveTab('1'); // Default to "All Events"
    }
  }, [hasKlaviyo, isManual, internalActiveTab, items]);

  const { isWhitelabelCompany, primaryColor } = useWhitelabelConfig();

  const tabsColor = isWhitelabelCompany
    ? primaryColor
    : 'var(--color-geekblue-6)';

  const chartData = useMemo(() => {
    const labels = data?.labels ?? [];
    const chartData = {
      labels,
      data: [] as (string | number)[][],
      color: [] as string[],
      title: [] as string[],
      fill: false,
    };

    // Map activeTab to display labels
    const getContactLabel = (tab: 'all' | 'new' | 'existing') => {
      switch (tab) {
        case 'all':
          return 'All Contacts';
        case 'new':
          return 'New Contacts';
        case 'existing':
          return 'Existing Contacts';
        default:
          return 'All Contacts';
      }
    };

    if (data) {
      // Find the selected tab's label
      const selectedTab = items.find(item => item?.key === internalActiveTab);
      const selectedLabel = selectedTab?.label;

      if (selectedLabel === 'All Events') {
        const allEventsData = data.datasets.find(d => d.label === 'All Events');
        if (allEventsData) {
          chartData.data = [allEventsData.data];
          chartData.color = [allEventsData.borderColor];
        }
        chartData.title = [getContactLabel(activeTab)];
      } else if (selectedLabel === 'Website visit') {
        const websiteVisitData = data.datasets.find(
          d => d.label === 'Website visit',
        );
        if (websiteVisitData) {
          chartData.data = [websiteVisitData.data];
          chartData.color = [websiteVisitData.borderColor];
        }
        chartData.title = [getContactLabel(activeTab)];
      } else if (selectedLabel === 'Active On Site') {
        const activeOnSiteData = data.datasets.find(
          d => d.label === 'Active On Site',
        );
        if (activeOnSiteData) {
          chartData.data = [activeOnSiteData.data];
          chartData.color = [activeOnSiteData.borderColor];
        }
        chartData.title = [getContactLabel(activeTab)];
      } else if (selectedLabel === 'Tracking Gap Klaviyo') {
        const trackingGapData = data.datasets.find(
          d => d.label === 'Tracking Gap Klaviyo',
        );
        if (trackingGapData) {
          chartData.data = [trackingGapData.data];
          chartData.color = [trackingGapData.borderColor];
        }
        chartData.title = [getContactLabel(activeTab)];
      } else if (selectedLabel === 'Product View') {
        const productViewData = data.datasets.find(
          d => d.label === 'Product View',
        );
        if (productViewData) {
          chartData.data = [productViewData.data];
          chartData.color = [productViewData.borderColor];
        }
        chartData.title = [getContactLabel(activeTab)];
      } else if (selectedLabel === 'Collection View') {
        const collectionViewData = data.datasets.find(
          d => d.label === 'Collection View',
        );
        if (collectionViewData) {
          chartData.data = [collectionViewData.data];
          chartData.color = [collectionViewData.borderColor];
        }
        chartData.title = [getContactLabel(activeTab)];
      } else if (selectedLabel === 'Add to Cart') {
        const addToCartData = data.datasets.find(
          d => d.label === 'Add to Cart',
        );
        if (addToCartData) {
          chartData.data = [addToCartData.data];
          chartData.color = [addToCartData.borderColor];
        }
        chartData.title = [getContactLabel(activeTab)];
      } else if (selectedLabel === 'Checkout Started') {
        const checkoutStartedData = data.datasets.find(
          d => d.label === 'Checkout Started',
        );
        if (checkoutStartedData) {
          chartData.data = [checkoutStartedData.data];
          chartData.color = [checkoutStartedData.borderColor];
        }
        chartData.title = [getContactLabel(activeTab)];
      } else if (selectedLabel === 'Return Visit') {
        const returnVisitData = data.datasets.find(
          d => d.label === 'Return Visit',
        );
        if (returnVisitData) {
          chartData.data = [returnVisitData.data];
          chartData.color = [returnVisitData.borderColor];
        }
        chartData.title = [getContactLabel(activeTab)];
      } else {
        // Default case
        const allEventsData = data.datasets.find(d => d.label === 'All Events');
        if (allEventsData) {
          chartData.data = [allEventsData.data];
          chartData.color = [allEventsData.borderColor];
        }
        chartData.title = [getContactLabel(activeTab)];
      }
    }
    return chartData;
  }, [internalActiveTab, data, activeTab, items]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>Visitor event data tracking</div>
        <Select
          defaultValue={selectedRangeOption}
          onChange={setSelectedRangeOption}
          options={signalRangeSelectOptions}
          popupMatchSelectWidth={false}
        />
      </div>
      <div className={styles.graphContainer}>
        <ConfigProvider
          theme={{
            components: {
              Tabs: {
                colorPrimary: tabsColor,
                colorPrimaryHover: tabsColor,
              },
            },
          }}
        >
          <Tabs
            items={items}
            onChange={key => setInternalActiveTab(key)}
            activeKey={internalActiveTab}
          />
          <Chart
            className={styles.chart}
            data={chartData}
            type="line"
            additionalOpts={{
              datasetOpts: { pointRadius: 4 },
              otherOpts: {
                legend: {
                  display: true,
                  align: 'center',
                  position: 'bottom',
                  labels: {
                    usePointStyle: true,
                    boxWidth: 8,
                    padding: 16,
                  },
                },
              },
            }}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};
